import { faIndent, faOutdent } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { chainCommands } from "prosemirror-commands";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, Plugin, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { createSameTypeAfter, createSameTypeBefore, setEmptyBlockToUnstyled, splitBlock } from "../commands";
import { Extension } from "../extension";
import { LIST_ITEM_TYPE_NAMES, PLAIN_TEXT_NODE_TYPE_NAMES } from "../nodes/plaintext";
import { blockNodesBetween, isSelectionFromStart } from "../utils";
import { getBlockBoundaryState } from "./blockBoundary";

export class Indent extends Extension {
	get name() {
		return "indent";
	}

	get defaultOptions() {
		return {
			levelPrefixClass: "prosemirror-indent",
			maxIndent: 6,
		};
	}

	keys() {
		return {
			Tab: this.indentLevel("up"),
			"Shift-Tab": this.indentLevel("down"),
			Backspace: this.lift(),
			"Mod-Backspace": this.lift(true),
			// Most of our enter key handling is here
			// Some has nothing to do with indentation
			// Maybe we should have a better place for this
			Enter: chainCommands(
				this.indentOnEnter(),
				setEmptyBlockToUnstyled(true),
				createSameTypeBefore,
				createSameTypeAfter(["header"]),
				splitBlock,
			),
		};
	}

	lift =
		(mod = false) =>
		(state: EditorState, dispatch?: (tr: Transaction) => void) => {
			// lift block if backspacing at the start.
			// if block is empty and type is not unstyled, don't handle it.
			const {
				empty,
				$from: { nodeBefore, nodeAfter, pos },
				$to,
			} = state.selection;
			const node = $to.node();
			const isUnstyled = node.type.name === "unstyled";
			const blockHasNoContent = node.content.size === 0;

			if (!isUnstyled && PLAIN_TEXT_NODE_TYPE_NAMES.includes(node.type.name)) return false;
			if (mod && !isUnstyled) return false; // let other plugin handle this (making unstyled)
			if (empty && !nodeBefore && (nodeAfter || isUnstyled)) {
				if ((node.attrs?.indent ?? 0) <= 0) return false;
				if (node.attrs.indent == 1 && LIST_ITEM_TYPE_NAMES.includes(node.type.name)) {
					return false;
				}
				if (dispatch) {
					if (mod || blockHasNoContent) {
						// cases that don't take into account nested blocks
						const indent = !mod ? node.attrs.indent - 1 : 0;
						if (indent < 0) return false;
						const tr = state.tr.setNodeMarkup(pos - 1, node.type, { ...node.attrs, indent });
						dispatch(tr.scrollIntoView());
					} else {
						return this.indentLevel("down")(state, dispatch);
					}
				}
				return true;
			}
			return false;
		};

	indentOnEnter = () => {
		return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
			const { selection } = state;
			if (!selection.empty) return false;
			if (!isSelectionFromStart(selection)) return false;
			const parent = selection.$from.parent;
			if (parent.textBetween(0, parent.content.size, undefined, "\n")) return false;

			const node = selection.$to.node();
			const isUnstyled = node.type.name === "unstyled";
			// special case the PLAIN_TEXT_NODE_TYPE_NAMES (except unstyled itself) because the desired behavior for them is to
			// be turned into an unstyled block, instead of dedented.
			if (!isUnstyled && PLAIN_TEXT_NODE_TYPE_NAMES.includes(node.type.name)) return false;

			return this.indentLevel("down", false)(state, dispatch);
		};
	};

	indentLevel(direction: string, alwaysReturnTrue = true) {
		return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
			const { blockTree } = getBlockBoundaryState(state) ?? {};
			const { $from, $to } = state.selection;
			const range = $from.blockRange($to);
			if (!range) return false;
			let tr = state.tr;
			let atLeastOneBlock = false;
			const parentStart = state.doc.resolve(range.start).start(range.depth);
			const seenBlockIds = new Set();

			const shouldIndentNode = (node: ProseMirrorNode) => {
				return !(
					seenBlockIds.has(node.attrs.blockId) ||
					node.attrs.indent === undefined ||
					(node.attrs.indent == 1 && LIST_ITEM_TYPE_NAMES.includes(node.type.name) && direction == "down") ||
					(node.attrs.indent <= 0 && direction == "down") ||
					(node.attrs.indent == this.options.maxIndent && direction == "up")
				);
			};
			const indentNode = (node: ProseMirrorNode, nodePosition: number) => {
				tr = tr.setNodeMarkup(nodePosition, node.type, {
					...node.attrs,
					indent: node.attrs.indent + (direction == "up" ? 1 : -1),
				});
				seenBlockIds.add(node.attrs.blockId);
				atLeastOneBlock = true;
			};

			blockNodesBetween(range.parent, range.start - parentStart, range.end - parentStart, (node, pos) => {
				const blockHasNoContent = node.content.size === 0;
				const blockTreeNode = blockTree?.getNodeByBlockKey(node.attrs.blockId);
				const descendants = Array.from(blockTreeNode?.descendants() || []);

				if (blockHasNoContent && shouldIndentNode(node)) {
					// If the block is empty, we can just indent it without worrying about its descendants
					indentNode(node, pos);
					return;
				}

				// Checking if indentation is allowed for the node and its descendants
				if (!shouldIndentNode(node)) {
					// If the parent can't indent (or more specifically dedent), then we don't want
					// any of its descendants to be analyzed.
					if (descendants) {
						for (const descendant of descendants) {
							if (descendant.block) {
								seenBlockIds.add(descendant.block.attrs.blockId);
							}
						}
					}
					return;
				}

				// The current node can be indented but before we start indenting, we need to make sure
				// that all of its descendants can be indented as well.
				if (descendants) {
					for (const descendant of descendants) {
						if (descendant.block && !shouldIndentNode(descendant.block)) {
							return;
						}
					}
				}

				// Now we can indent the current node and all of its descendants.
				indentNode(node, pos);
				if (descendants) {
					for (const descendant of descendants) {
						if (descendant.block) {
							indentNode(descendant.block, descendant.start);
						}
					}
				}
			});

			if (dispatch && atLeastOneBlock) {
				dispatch(tr);
			}
			return alwaysReturnTrue || atLeastOneBlock;
		};
	}

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				props: {
					decorations: (state: EditorState): DecorationSet => {
						const decorations: Decoration[] = [];

						const decorate = (node: ProseMirrorNode, pos: number) => {
							if (node.type.isBlock && node.attrs.indent) {
								decorations.push(
									Decoration.node(pos, pos + node.nodeSize, {
										class: cx(
											extension.options.levelPrefixClass,
											`${extension.options.levelPrefixClass}-${node.attrs.indent}`,
											`${extension.options.levelPrefixClass}-${node.type.name}`,
											{
												[`${extension.options.levelPrefixClass}-plain-text`]: PLAIN_TEXT_NODE_TYPE_NAMES.includes(
													node.type.name,
												),
											},
										),
										"data-indent": `${node.attrs.indent}`,
										"prosemirror-indent": `${node.attrs.indent}`,
									}),
								);
							}
							return false;
						};

						state.doc.descendants(decorate);
						return DecorationSet.create(state.doc, decorations);
					},
				},
			}),
		];
	}

	menuEntry() {
		return [
			{
				icon: faIndent,
				title: "Indent",
				run: () => {
					return this.indentLevel("up");
				},
				section: "indent" as const,
				priority: 1,
			},
			{
				icon: faOutdent,
				title: "Outdent",
				run: () => {
					return this.indentLevel("down");
				},
				section: "indent" as const,
				priority: 0,
			},
		];
	}
}

export default Indent;
